import React from 'react';
import { Headphones, Mail, MessageCircle, Phone, Clock, HelpCircle, ArrowRight } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const Support = () => {
  const supportOptions = [
    {
      icon: <MessageCircle size={24} />,
      title: "Live Chat",
      description: "Get instant help from our support team",
      color: "#3B82F6",
      gradient: "linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)",
      available: "Available 24/7",
      features: ["Instant messaging", "Real-time support", "Screen sharing"]
    },
    {
      icon: <Phone size={24} />,
      title: "Phone Support",
      description: "Speak directly with our experts",
      color: "#10B981",
      gradient: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
      available: "Mon-Fri 9AM-6PM",
      features: ["Direct calling", "Expert consultation", "Priority support"]
    },
    {
      icon: <Mail size={24} />,
      title: "Email Support",
      description: "Send us your questions and we'll respond quickly",
      color: "#8B5CF6",
      gradient: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)",
      available: "Response within 24 hours",
      features: ["Detailed responses", "File attachments", "Ticket tracking"]
    },
    {
      icon: <HelpCircle size={24} />,
      title: "Help Center",
      description: "Browse our comprehensive knowledge base",
      color: "#F59E0B",
      gradient: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
      available: "Self-service resources",
      features: ["Documentation", "Video tutorials", "FAQ database"]
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Support Center"
        description="Get help with CAM360. Choose the support option that works best for you."
      />
      <div className="page-container">
        {/* Support Options Cards */}
        <div 
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          {supportOptions.map((option, index) => (
            <div
              key={index}
              className="asset-landing-card"
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--primary-color)`,
                  width: '100%'
                }}
              />
              
              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: 'var(--radius-lg)',
                      background: `${option.color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: option.color }} className="asset-card-icon">
                      {option.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{ 
                      fontSize: '18px', 
                      fontWeight: '600', 
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {option.title}
                    </h4>
                  </div>
                  <ArrowRight 
                    size={20} 
                    className="asset-card-arrow"
                  />
                </div>
                
                {/* Description */}
                <p style={{ 
                  fontSize: '13px', 
                  color: 'var(--text-secondary)',
                  margin: '0 0 12px 0',
                  lineHeight: '1.4',
                  fontFamily: 'var(--font-family)'
                }}>
                  {option.description}
                </p>

                {/* Availability */}
                <div style={{ 
                  fontSize: '11px', 
                  fontWeight: '500', 
                  color: option.color,
                  marginBottom: '6px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  {option.available}
                </div>

                {/* Key Features */}
                <div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '500', 
                    color: '#374151',
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em'
                  }}>
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {option.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '11px',
                          padding: '3px 6px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '4px',
                          fontWeight: '500'
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Immediate Assistance Section */}
        <div className="card-cam" style={{ 
          marginTop: '32px',
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <Headphones size={24} color="#EC4899" />
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#1F2937',
              margin: 0,
              fontFamily: 'var(--font-family)'
            }}>
              Need Immediate Assistance?
            </h3>
          </div>
          <p style={{
            fontSize: '14px',
            color: '#6B7280',
            margin: 0,
            marginBottom: '16px',
            fontFamily: 'var(--font-family)'
          }}>
            Our support team is here to help you get the most out of CAM360. 
            Contact us through any of the channels above for personalized assistance.
          </p>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: '#6B7280',
            fontFamily: 'var(--font-family)'
          }}>
            <Clock size={16} />
            <span>Average response time: 2 hours</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Support;
